import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { LogLevel, LogService } from "@/services/logging";
import NotificationService from "@/services/notification.service";
import { resolveEndpoint } from "@/config/endpoint-resolver";
import AppModule from "@/store/modules/app";
import BookingModule from "@/store/modules/booking";
import CashFloatModule from "@/store/modules/cash-float";
import ComplianceModule from "@/store/modules/compliance";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { PaymentMethods } from "@/types/db/accounting";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  Compliance,
  EventResponse,
  EventType,
  MoneyInOutType,
  CashLocation,
} from "@smeetz/compliance-common";
import { OrderSessionState, PaymentMethod } from "@smeetz/order-common";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import {
  isBelgianCompliant,
  isCroatianCompliant,
  isFrenchCompliant,
  isGermanCompliant,
  isSpanishCompliant,
} from "../vue-helpers/compliance";
import { getSmtzAuth, getUserAccessToken } from "./auth";
import { getLanguage } from "./lang";
import { persistComplianceFields } from "./store";
import {
  convertBookingToOrderSession,
  findLastSignedPOSOrderSession,
  getSessionOrderType,
} from "./order-session";
import { isOrderSessionSigned } from "@/api/gql/badges";

export const EFR_SERVER_URL =
  import.meta.env.VITE_COMPLIANCE_ENDPOINT || "http://localhost:5618";

export async function testEfrConnection(timeoutMs = 5000): Promise<boolean> {
  const { compliance } = ComplianceModule;
  if (
    (!isSpanishCompliant.value &&
      !isFrenchCompliant.value &&
      !isCroatianCompliant.value) ||
    !compliance
  ) {
    // no connection outside Spain, France, or Croatia or if compliance package not initialized
    return false;
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(EFR_SERVER_URL, { signal: controller.signal });
    if (response.ok) {
      ComplianceModule.setComplianceStatus({ efrConnection: true });
    }
    return response.ok;
  } catch (error) {
    ComplianceModule.setComplianceStatus({ efrConnection: false });
    // If the error is due to the abort or a network issue, we consider the server unreachable
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Tests the connection to the Belgian FDM.
 *
 * Unlike the EFR countries, which probe a local HTTP server, Belgium asks the
 * compliance package to talk to the fiscal data module directly.
 */
export async function testFdmConnection(): Promise<boolean> {
  try {
    const { compliance } = ComplianceModule;
    if (!isBelgianCompliant.value || !compliance) {
      // no FDM outside Belgium, or compliance package not initialized
      return false;
    }
    const isConnected = await compliance.testComplianceDeviceConnection();
    ComplianceModule.setComplianceStatus({ fdmConnection: isConnected });
    return isConnected;
  } catch (error) {
    ComplianceModule.setComplianceStatus({ fdmConnection: false });
    LogService.info("Compliance: Failed to check FDM connection", { error });
    return false;
  }
}

/**
 * Re-checks the fiscal device connection for the current country, so the
 * selling-disabled overlay reacts as soon as an event fails. Belgium checks the
 * FDM, the EFR countries check the EFR; everywhere else this is a no-op.
 */
async function refreshFiscalDeviceStatus(): Promise<boolean> {
  if (isBelgianCompliant.value) {
    return testFdmConnection();
  }
  return testEfrConnection();
}

/**
 * Generates a datetime in the format the FDM expects:
 * `YYYY-MM-DDTHH:mm:ss±HH:MM`.
 *
 * Deliberately built from local-time getters rather than an ISO/UTC string:
 * the FDM requires the POS wall-clock time with an explicit offset.
 * @param date defaults to now
 */
export const generateFdmDateTime = (date?: Date): string => {
  if (!date) {
    date = new Date();
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // Timezone offset formatted as +01:00 / -05:00
  const offset = date.getTimezoneOffset();
  const offsetHours = Math.abs(Math.floor(offset / 60));
  const offsetMinutes = Math.abs(offset % 60);
  const offsetSign = offset <= 0 ? "+" : "-";
  const offsetString = `${offsetSign}${String(offsetHours).padStart(
    2,
    "0"
  )}:${String(offsetMinutes).padStart(2, "0")}`;

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetString}`;
};

/**
 * Initializes compliance library
 */
export async function initializeComplianceLib() {
  const token = getUserAccessToken();
  if (!token) {
    LogService.info(
      "Compliance: No token found when initialiating compliance lib, aborting"
    );
    return;
  }
  if (
    !isGermanCompliant.value &&
    !isSpanishCompliant.value &&
    !isFrenchCompliant.value &&
    !isCroatianCompliant.value &&
    !isBelgianCompliant.value
  ) {
    LogService.info(
      "Compliance: Unsupported country, aborting event processing"
    );
    return;
  }

  if (!DeviceModule.hardwareLocation?.deviceUid) {
    LogService.info("Compliance: Failed to process event, unregistered device");
    ComplianceModule.setComplianceStatus({
      packageInit: false,
      efrConnection: false,
      fdmConnection: false,
    });
    throw new Error("Failed to process event, unregistered device");
  }
  const smeetzAuth = await getSmtzAuth();
  if (!smeetzAuth) {
    throw new Error("No smtzAuth token found, aborting compliance init");
  }

  try {
    const compliance = new Compliance({
      country: POSConfigModule.getGroupCountryCode as
        | "BE"
        | "FR"
        | "ES"
        | "DE"
        | "HR", // limit the type to the supported countries, as conditions above already check for that
      certificationServiceUrl: resolveEndpoint("VITE_CERTIFICATION_URL"),
      deviceUid: DeviceModule.hardwareLocation.deviceUid,
      language: getLanguage().toUpperCase(),
      posSwVersion: import.meta.env.VITE_APP_VERSION || "",
      terminalId: isCroatianCompliant.value
        ? String(DeviceModule.hardwareLocation.deviceId)
        : DeviceModule.hardwareLocation.deviceUid,
      logServerUrl: import.meta.env.VITE_LOG_API,
      smeetzApiUrl: resolveEndpoint("VITE_GQL_API"),
      smeetzAuth,
      complianceEndpoint: EFR_SERVER_URL,
    });
    // Authorize (required for both countries)
    await compliance.authorize(token);
    // Initialize the compliance system
    const configResult = await compliance.initialize();
    if (!configResult.success) {
      console.log("Failed to initialize compliance system", configResult.error);
      NotificationService.showCustomError(
        t("compliance.compliance-lib-init-failed-global")
      );
      throw configResult.error;
    }
    ComplianceModule.setComplianceObj({ compliance });
    if (isBelgianCompliant.value) {
      const isFdmConnected = await testFdmConnection();
      ComplianceModule.setComplianceStatus({
        packageInit: true,
        fdmConnection: isFdmConnected,
      });
    } else {
      const isEfrConnected = await testEfrConnection();
      ComplianceModule.setComplianceStatus({
        packageInit: true,
        efrConnection: isEfrConnected,
      });
    }
  } catch (error) {
    ComplianceModule.setComplianceStatus({
      packageInit: false,
      efrConnection: false,
      fdmConnection: false,
    });
    LogService.info("Compliance: Failed to initialize compliance lib", {
      error,
    });
    throw error;
  }
}

/**
 * Validates info needed to process compliance events,
 * throws an error if at least one is not found
 * returns NULL, if called in an unsupported country
 */
export async function validateComplianceRequirements(): Promise<{
  transactionNumber: string;
  operatorId: string;
  operatorName: string;
  operatorTin: string;
  operatorFiscalId: string;
  compliance: Compliance;
  bookingPeriodId?: string;
  posDateTime: string;
} | null> {
  if (
    !isGermanCompliant.value &&
    !isSpanishCompliant.value &&
    !isFrenchCompliant.value &&
    !isCroatianCompliant.value &&
    !isBelgianCompliant.value
  ) {
    LogService.info(
      "Compliance: Unsupported country, aborting event processing"
    );
    return null;
  }

  if (!DeviceModule.hardwareLocation?.deviceUid) {
    LogService.info("Compliance: Failed to process event, unregistered device");
    // users should not be able to use compliance features if device is not registered
    ComplianceModule.setComplianceStatus({
      packageInit: false,
    });
    throw new Error("Failed to process event, unregistered device");
  }

  if (!ComplianceModule.compliance) {
    // if not initialized, try to initialize it
    try {
      await initializeComplianceLib();
    } catch (error) {
      LogService.info("Compliance: Failed to initialize compliance lib", {
        error,
      });
      throw new Error(`Failed to initialize: ${error}`);
    }
    // check again if the compliance module is initialized
    if (!ComplianceModule.compliance) {
      LogService.info(
        "Compliance: module is not initialized after re-trying to initialize it"
      );
      throw new Error("Compliance module is not initialized.");
    }
  }

  if (!UserModule.userId) {
    throw new Error(
      "User ID is missing. Cannot proceed with event processing."
    );
  }

  const transactionNumber = ComplianceModule.transactionNumber?.toString();
  LogService.info(`Compliance: transactionNumber: ${transactionNumber}`);
  if (!transactionNumber) {
    throw new Error(
      "Transaction number is missing. Cannot proceed with event processing."
    );
  }

  const bookingPeriodId = CashFloatModule.cashFloat?.uid;

  const operatorFiscalId =
    UserModule.user.socialSecurityNumber || UserModule.userId.toString();
  const operatorTin = operatorFiscalId;
  // Belgium stamps every event with the POS wall-clock time; the other
  // countries derive it at the call site or not at all.
  const posDateTime = generateFdmDateTime();
  LogService.info("Compliance: current compliance data", {
    transactionNumber,
    operatorId: UserModule.userId.toString(),
    operatorTin,
    operatorFiscalId,
    operatorName: UserModule.user.fullname,
    compliance: ComplianceModule.compliance,
    bookingPeriodId,
    posDateTime,
  });

  return {
    transactionNumber,
    operatorId: UserModule.userId.toString(),
    operatorTin,
    operatorFiscalId,
    operatorName: UserModule.user.fullname,
    compliance: ComplianceModule.compliance,
    bookingPeriodId,
    posDateTime,
  };
}

/**
 * Handles steps needed after a fiscal event is processed, i.e increasing the transaction number and displaying any messages/errors returned
 * @param result
 */
export function handleEventSent(
  result: EventResponse,
  showErrorNotification = true
) {
  ComplianceModule.increaseTransactionNumber();
  persistComplianceFields();
  if (result.data?.message && isBelgianCompliant.value) {
    NotificationService.showCustomInfoWithTitle(
      result.data?.message,
      t("compliance.fdm-msg")
    );
  } else if (
    result.data?.message &&
    (isSpanishCompliant.value ||
      isFrenchCompliant.value ||
      isCroatianCompliant.value)
  ) {
    NotificationService.showCustomInfoWithTitle(
      result.data?.message,
      t("compliance.efr-msg")
    );
  }
  if (result.error) {
    LogService.info(
      `Compliance: Error processing ${POSConfigModule.getGroupCountryCode} compliance event`,
      {
        error: result.error,
      }
    );
    if (showErrorNotification) {
      NotificationService.showCustomError(result.error);
    }
  }
}

/**
 * Processes Start Sale event (Germany)
 * @param booking Booking to process
 * @param orderSessionId Order session ID to process
 */
export async function processStartSaleEvent(
  booking: IProductSlotBooking,
  orderSessionId: string
) {
  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return;
  }
  if (!isGermanCompliant.value) {
    // Germany only
    return;
  }
  const {
    transactionNumber,
    operatorId,
    operatorName,
    compliance,
    bookingPeriodId,
  } = complianceData;
  let orderSession;
  try {
    orderSession = await convertBookingToOrderSession(booking, orderSessionId);
  } catch (error) {
    LogService.info("Compliance: Failed to convert booking to order session", {
      error,
    });
    throw new Error(`Failed to convert booking to order session`);
  }

  if (!orderSession) {
    LogService.info(
      "Compliance: Undefined order session while processing sale, aborting"
    );
    throw new Error(`Undefined order session while processing sale, aborting`);
  }

  const startSaleEvent = {
    data: {
      orderSession,
      transactionNumber,
      operatorId,
      operatorName,
      bookingPeriodId,
    },
  };

  LogService.info("Compliance: Processing start sale event", {
    startSaleEvent,
  });

  const result = await compliance.processEvent(
    EventType.START_SALE,
    startSaleEvent
  );
  handleEventSent(result);
  if (!result.success) {
    throw result.error;
  }

  LogService.info(
    "Compliance: Start sale processed successfully:",
    result.data
  );
  return result;
}

/**
 * Processes Sale event
 * @param booking Booking to process
 * @param orderSessionId Order session ID to process
 */
export async function processSaleEvent(
  booking: IProductSlotBooking,
  orderSessionId: string
) {
  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return;
  }
  const {
    transactionNumber,
    operatorId,
    operatorName,
    operatorFiscalId,
    compliance,
    bookingPeriodId,
    posDateTime,
  } = complianceData;
  let orderSession;
  try {
    orderSession = await convertBookingToOrderSession(booking, orderSessionId);
  } catch (error) {
    LogService.info("Compliance: Failed to convert booking to order session", {
      error,
    });
    throw new Error(`Failed to convert booking to order session`);
  }

  if (!orderSession) {
    LogService.info(
      "Compliance: Undefined order session while processing sale, aborting"
    );
    throw new Error(`Undefined order session while processing sale, aborting`);
  }

  if (
    orderSession.orderLines.length === 0 &&
    orderSession.payments.length === 0 &&
    (orderSession.vouchers?.length || 0) === 0 &&
    (orderSession.discounts?.length || 0) === 0
  ) {
    LogService.info(
      "Compliance: Empty order session while processing sale, aborting"
    );
    return;
  }

  const hasCancellation = orderSession.orderLines.some(
    (orderLine) => orderLine.quantity < 0
  );
  if (hasCancellation && !navigator.onLine) {
    // cancellation requires an internet connection, sale will not be processed
    LogService.info(
      "Compliance: Sale with cancellation requires an internet connection, aborting sale processing"
    );
    NotificationService.showCustomError(
      t("compliance.sale-cancellation-requires-internet")
    );
    throw new Error(
      "Sale with cancellation requires an internet connection, aborting sale processing"
    );
  }

  const orderType = getSessionOrderType(booking, orderSession);

  const saleEvent: { data: Record<string, unknown> } = {
    data: {
      orderSession,
      transactionNumber,
      operatorId,
      operatorName,
      operatorFiscalId,
      orderType,
      bookingPeriodId,
    },
  };

  if (isBelgianCompliant.value) {
    saleEvent.data.posDateTime = posDateTime;
    // Ticket removals and quantity changes made before the sale was completed.
    saleEvent.data.changes =
      BookingModule.bookingChanges[booking.uuid || ""] || undefined;
    // The FDM always signs sales as PAPER: a paper receipt is printed for every
    // Belgian sale, which is why auto-print is forced on there.
    saleEvent.data.ticketMedium = "PAPER";
  }

  LogService.info("Compliance: Processing sale event", { saleEvent });

  const result = await compliance.processEvent(EventType.SALE, saleEvent);
  handleEventSent(result);
  if (!result.success) {
    if (isBelgianCompliant.value) {
      // re-check the FDM so the selling-disabled overlay reacts immediately
      await testFdmConnection();
    }
    throw result.error;
  }

  LogService.info("Compliance: Sale processed successfully:", result.data);
  // Set signature info in the order session
  const orderSessionToUpdate = booking.orderSessions.find(
    (os) => os.id === orderSessionId
  );
  if (orderSessionToUpdate) {
    const orderSessionCopy = cloneDeep(orderSessionToUpdate);
    if (isGermanCompliant.value) {
      const signatureInfo = {
        fiscalNumber: result.data?.fiscalNumber,
        signature: result.data?.signature,
        sec: result.data?.sec,
        message: result.data?.message,
        transactionId: result.data?.transactionId,
        revision: result.data?.revision,
        raw: result.data?.raw,
      };
      orderSessionCopy.signatureInfoDE = signatureInfo;
    } else if (isSpanishCompliant.value) {
      const signatureInfo = {
        signature: result.data?.signature,
        fiscalNumber: result.data?.fiscalNumber,
        sec: result.data?.sec,
        timestamp: result.data?.timestamp,
      };
      orderSessionCopy.signatureInfoES = signatureInfo;
    } else if (isFrenchCompliant.value) {
      const { documentType, operationType } = parseDocumentxOperationType(
        result.data?.raw?.TraC?.Fis?.Payload
      );
      const signatureInfo = {
        signature: result.data?.signature,
        fiscalNumber: result.data?.fiscalNumber,
        sec: result.data?.sec,
        documentType: documentType,
        operationType: operationType,
      };
      orderSessionCopy.signatureInfoFR = signatureInfo;
    } else if (isCroatianCompliant.value) {
      const signatureInfo = {
        fiscalNumber: result.data?.fiscalNumber,
        zki: result.data?.signature ?? result.data?.zkiCode,
        jir: result.data?.jir ?? undefined,
        pkp: result.data?.pKpCode ?? undefined,
        qrCodeUrl:
          result.data?.qrCode ?? result.data?.rawResponse?.TraC?.Fis?.Code,
        oib: result.data?.oib,
        timestamp: result.data?.timestamp,
      };
      orderSessionCopy.signatureInfoHR = signatureInfo;
    } else if (isBelgianCompliant.value) {
      const signatureInfo = {
        signature: result.data?.signature,
        fiscalNumber: result.data?.fiscalNumber,
        sec: result.data?.sec,
        fdmRef: result.data?.fdmRef,
        deviceId: result.data?.deviceId,
        posId: result.data?.posId,
        terminalId: result.data?.terminalId,
        posFiscalTicketNo: result.data?.posFiscalTicketNo,
        fdmVersion: result.data?.fdmSwVersion,
        posDateTime: result.data?.posDateTime,
        // present on cancellations: points at the transaction being reversed
        originalTransactionFdmRef: result.data?.originalTransactionFdmRef,
        locationCbe: result.data?.locationCbe,
        footer: result.data?.footer,
        // FDM-computed VAT breakdown; overrides the POS-computed lines on the receipt
        vat: result.data?.vat,
      };
      orderSessionCopy.signatureInfoBE = signatureInfo;
    }

    orderSessionCopy.orderType = orderType;
    BookingModule.updateOrderSession({
      orderSessionId: orderSessionId,
      newOrderSession: orderSessionCopy,
      bookingUuid: booking.uuid as string,
    });
  }
  return result;
}

/**
 * Processes Additional events that don't require an order session
 * WORK IN/OUT, DRAWER OPEN/CLOSE ... etc
 * @param type
 * @param additionalData any extra fields neeeded for the event
 */
export async function processAdditionalEvents(
  type: EventType,
  additionalData?: any
) {
  // Events only France and Belgium report; the other certified countries
  // do not expect them.
  const franceAndBelgiumOnlyEvents = [
    EventType.PRICE_LOOKUP,
    EventType.SUSPEND_RECEIPT,
    EventType.RESUME_RECEIPT,
    EventType.DRAWER_OPEN,
  ];
  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return;
  }
  if (
    franceAndBelgiumOnlyEvents.includes(type) &&
    !isFrenchCompliant.value &&
    !isBelgianCompliant.value
  ) {
    return;
  }
  const {
    transactionNumber,
    operatorId,
    operatorName,
    operatorTin,
    operatorFiscalId,
    compliance,
    bookingPeriodId,
    posDateTime,
  } = complianceData;
  const event = {
    data: {
      transactionNumber,
      operatorId,
      operatorName,
      // Belgium does not report operatorTin: the FDM knows the operator only as
      // employeeId, which the package maps from operatorFiscalId. Since the two
      // hold the same value, sending both would put the operator's social
      // security number into the signed payload a second time, under a key the
      // Belgian mapper does not strip.
      ...(!isBelgianCompliant.value && { operatorTin }),
      operatorFiscalId,
      bookingPeriodId,
      // Belgium stamps every event with the POS wall-clock time, and the FDM
      // derives the booking period from it. Without it the package falls back
      // to a UTC ISO string, which is not the offset format the FDM expects.
      ...(isBelgianCompliant.value && { posDateTime }),
      ...additionalData, // Spread any additional data needed for the event
    },
  };
  LogService.info("Compliance: processing additional event", { type, event });
  // execute authorization before processing WORK_IN event to ensure token is valid
  if (type === EventType.WORK_IN) {
    try {
      const token = getUserAccessToken();
      if (!token) {
        LogService.info(
          "Compliance: No token found when authorizing compliance lib, aborting"
        );
        throw new Error("No token found when authorizing compliance lib");
      }
      event.data.authToken = token;
    } catch (error) {
      LogService.info(
        "Compliance: Failed to authorize compliance lib before WORK_IN event",
        { error }
      );
      throw new Error(
        `Failed to authorize compliance lib before WORK_IN event: ${error}`
      );
    }
  }
  const result = await compliance.processEvent(type, event);
  handleEventSent(result);
  if (
    (isGermanCompliant.value || isBelgianCompliant.value) &&
    type === EventType.WORK_IN &&
    result.isBookingPeriodClosed
  ) {
    // increase transaction number again if the booking period was closed
    ComplianceModule.increaseTransactionNumber();
  }
  if (!result.success) {
    // in case of error, test the connection again
    await refreshFiscalDeviceStatus();
    throw new Error(result.error);
  }
  return result;
}

/**
 * Downloads a CSV the browser never fetched, by handing it a blob URL.
 */
function downloadCsv(csvContent: string, fileName: string) {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Clicking only queues the download, so the URL has to outlive this tick;
  // revoking it inline can leave the browser resolving a blob that is gone.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Exports the Belgian fiscal archive as a CSV.
 *
 * The FPS can ask for the full event archive at any time, so this is a
 * regulatory obligation rather than a reporting convenience. The certification
 * service builds the file; the POS only has to save it.
 */
export async function exportFiscalArchive() {
  AppModule.updatedIsLoading(true);
  try {
    LogService.info("Compliance: exporting fiscal archive");
    const result = await processAdditionalEvents(
      EventType.EXPORT_FISCAL_ARCHIVE
    );
    const { csvContent, fileName } = (result?.data || {}) as {
      csvContent?: string;
      fileName?: string;
    };
    if (!csvContent || !fileName) {
      LogService.info(
        "Compliance: no data received when exporting the fiscal archive",
        { result }
      );
      NotificationService.showCustomError(t("compliance.fiscal-archive-error"));
      return;
    }
    downloadCsv(csvContent, fileName);
    NotificationService.showCustomSuccess(
      t("compliance.fiscal-archive-exported")
    );
  } catch (error) {
    LogService.info("Compliance: error exporting the fiscal archive", {
      error,
    });
    NotificationService.showCustomError(t("compliance.fiscal-archive-error"));
  } finally {
    AppModule.updatedIsLoading(false);
  }
}

/**
 * Answers two separate questions about a receipt, from one pass over the
 * booking.
 *
 * **Which session to render.** Normally the current one. The exception is a
 * reprint: reopening a booking to print another copy creates a NEW, empty order
 * session, so rendering that would produce a blank document. The receipt then
 * shows the last sale the booking actually holds, *whether or not it was
 * signed* — a sale that failed to sign still happened, and the operator asked
 * for a receipt of it.
 *
 * **Which session a REPRINT event may reference.** Only a signed one: there is
 * no transaction for the fiscal device to duplicate otherwise. That is
 * {@link findLastSignedPOSOrderSession}, which {@link checkAndProcessReprint}
 * uses to decide between a copy and a first print.
 *
 * The two can differ, deliberately. When the sale being rendered was never
 * signed, no REPRINT event is sent and the receipt goes out with
 * `isSigned = false`, still showing that sale.
 */
export async function resolveReceiptOrderSession(
  booking: IProductSlotBooking
): Promise<{
  /**
   * The order session whose contents the receipt should render. null means no
   * sale could be found for this booking, so there is nothing to print.
   */
  orderSessionId: string | null;
  isEmptyOrderSession: boolean;
  /**
   * The last order session signed on the POS, when the current one is empty.
   * null means nothing was ever signed for this booking, which makes the print
   * a first print rather than a copy.
   */
  signedOrderSessionId: string | null;
  /** The certification DB could not be consulted, so the print must abort. */
  undetermined?: boolean;
}> {
  const currentOrderSessionId = BookingModule.currentOrderSessionId;

  /**
   * A session is empty when it has no tickets and no payments linked to it.
   * The same test decides both whether to look further back and which session
   * to look back to, so the two can never disagree on what empty means.
   */
  const hasContents = (orderSessionId: string | null) =>
    booking.pssList.some((pss) => pss.orderSessionId === orderSessionId) ||
    booking.accountings.some(
      (acc) =>
        acc.paymentMethod !== PaymentMethods.METHODS_POS_FREE &&
        acc.orderSessionId === orderSessionId
    );

  const isEmptyOrderSession = !hasContents(currentOrderSessionId);

  if (!isEmptyOrderSession || !currentOrderSessionId) {
    return {
      orderSessionId: currentOrderSessionId,
      isEmptyOrderSession,
      signedOrderSessionId: null,
    };
  }

  /**
   * The current session is empty, so walk back to the most recent completed
   * session that holds something. Sorted newest first: the latest is the
   * current empty one, and the first with contents behind it is the sale being
   * copied.
   */
  const lastNonEmptySession = booking.orderSessions
    .filter((os) => os.state === OrderSessionState.COMPLETED)
    .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
    .find((session) => hasContents(session.id));

  const { session, undetermined } = await findLastSignedPOSOrderSession(
    booking,
    currentOrderSessionId
  );

  return {
    orderSessionId: lastNonEmptySession?.id ?? null,
    isEmptyOrderSession,
    signedOrderSessionId: undetermined ? null : session?.id ?? null,
    ...(undetermined ? { undetermined: true } : {}),
  };
}

/**
 * First checks if a given order will result in a reprint, processes REPRINT event in case it is
 * The check is done by checking if the order session is:
 * 1- Empty, which means that no edits were done to the booking, and the printed receipt will be a duplicate in terms of tickets and payments
 * 2- Already has a receipt printed
 *
 * In both cases there also has to be a signed order session for the EFR to reprint. When there is
 * none, nothing was ever fiscalized for this booking, everything unfiscalized is absorbed into the
 * current order session by convertBookingToOrderSession, and this is a first print rather than a
 * reprint, so requiresReprint is false and no event is sent.
 *
 * undeterminedFiscalState is returned when the certification DB could not be consulted and the
 * booking carries POS history this device did not sign, which leaves the receipt impossible to
 * classify as either a first print or a duplicate. Callers must not print in that case.
 * @param booking
 * @param reason which defaults to "Customer Request"
 */
export async function checkAndProcessReprint(
  booking: IProductSlotBooking,
  reason = "Customer Request"
): Promise<{
  requiresReprint: boolean;
  /**
   * The order session the reprint refers to. For an empty current session this
   * is the original session being copied, which the Belgian receipt builder
   * needs in order to render the copied sale rather than an empty one.
   */
  orderSessionToReprint?: string | null;
  result?: EventResponse;
  error?: string;
  undeterminedFiscalState?: boolean;
}> {
  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return { requiresReprint: false };
  }
  const {
    operatorId,
    operatorName,
    operatorFiscalId,
    compliance,
    transactionNumber,
    posDateTime,
  } = complianceData;

  const currentOrderSessionId = BookingModule.currentOrderSessionId;
  const currentOrderSession = booking.orderSessions.find(
    (os) => os.id === currentOrderSessionId
  );
  if (!currentOrderSessionId || !currentOrderSession) {
    throw new Error("No current order session found");
  }

  const currentOrderSessionCopy = cloneDeep(currentOrderSession);

  const { signedOrderSessionId, isEmptyOrderSession, undetermined } =
    await resolveReceiptOrderSession(booking);

  if (!currentOrderSession.receiptPrinted && !isEmptyOrderSession) {
    // not a reprint event
    LogService.info(
      "Compliance: Receipt printed for first time, or order had changes, aborting processing the REPRINT event",
      {
        isEmptyOrderSession,
        receiptPrinted: currentOrderSessionCopy.receiptPrinted,
      }
    );
    return { requiresReprint: false };
  }

  let orderSessionToReprint = currentOrderSessionId;

  if (isEmptyOrderSession) {
    /**
     * An empty order session carries no fiscal data of its own, so the REPRINT event has to
     * reference the last order session that was actually signed on the POS. resolveReceiptOrderSession
     * already looked that up, and the same lookup decides the cross-session fiscalization cut-off,
     * which guarantees that the session we reprint is both the session the receipt renders and the
     * session convertBookingToOrderSession considers already signed.
     */
    if (undetermined) {
      LogService.info(
        "Compliance: Could not determine whether this booking was already signed on the POS, aborting the print."
      );
      return { requiresReprint: false, undeterminedFiscalState: true };
    }

    if (!signedOrderSessionId) {
      /**
       * Nothing was ever signed on the POS for this booking, so there is no original receipt to
       * duplicate. Everything unfiscalized is absorbed into the current order session by
       * convertBookingToOrderSession, which makes this a first print rather than a reprint.
       */
      LogService.info(
        "Compliance: No signed POS order session found for this booking, handling it as a new print instead of a reprint."
      );
      return { requiresReprint: false };
    }

    orderSessionToReprint = signedOrderSessionId;
    LogService.info("Compliance: order session to reprint", {
      orderSessionToReprint,
    });
  } else {
    // only reached when the receipt was printed before, so the current session is the original.
    // It still has to be signed, otherwise there is no transaction for the EFR to reprint.
    const signed = await isOrderSessionSigned(currentOrderSessionId);

    if (signed === undefined) {
      LogService.info(
        "Compliance: Could not determine whether the current order session is signed, aborting the print.",
        { currentOrderSessionId }
      );
      return { requiresReprint: false, undeterminedFiscalState: true };
    }

    if (!signed) {
      LogService.info(
        "Compliance: Current order session was printed before but was never signed, handling it as a new print instead of a reprint.",
        { currentOrderSessionId }
      );
      return { requiresReprint: false };
    }
  }

  const event = {
    data: {
      orderSessionId: orderSessionToReprint,
      operatorId,
      operatorName,
      reason,
      transactionNumber,
      ...(isBelgianCompliant.value ? { operatorFiscalId, posDateTime } : {}),
    },
  };

  LogService.info("Compliance: Processing reprint event", {
    isEmptyOrderSession,
    receiptPrinted: currentOrderSessionCopy.receiptPrinted,
    event,
  });

  const result = await compliance.processEvent(EventType.REPRINT, event);
  // the compliance lib reports a missing original as "Could not find original transaction
  // reference", optionally suffixed with " with fiscal number". Reaching that here means the
  // reprint target was verified as signed but the EFR still cannot resolve it, so it is not
  // worth a notification of its own, unlike a genuine EFR fault.
  handleEventSent(
    result,
    !result.error?.startsWith("Could not find original transaction")
  );
  if (!result.success) {
    await refreshFiscalDeviceStatus();
    return {
      requiresReprint: true,
      orderSessionToReprint,
      result,
      error: result.error,
    };
  }
  return { requiresReprint: true, orderSessionToReprint, result };
}

/**
 * Processes Abort event
 * @param booking Booking to process
 * @param orderSessionId Order session ID to process
 */
export async function processAbortEvent(
  booking: IProductSlotBooking,
  orderSessionId: string
) {
  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return;
  }
  const {
    transactionNumber,
    operatorId,
    operatorName,
    operatorFiscalId,
    compliance,
    bookingPeriodId,
    posDateTime,
  } = complianceData;
  let orderSession;
  try {
    orderSession = await convertBookingToOrderSession(booking, orderSessionId);
  } catch (error) {
    LogService.info("Compliance: Failed to convert booking to order session", {
      error,
    });
    throw new Error(`Failed to convert booking to order session`);
  }

  if (!orderSession) {
    LogService.info("Compliance: Undefined order session");
    throw new Error(`Undefined order session`);
  }

  const orderType = getSessionOrderType(booking, orderSession);

  const abortEvent: { data: Record<string, unknown> } = {
    data: {
      orderSession,
      transactionNumber,
      orderType,
      operatorId,
      operatorName,
      bookingPeriodId,
    },
  };

  if (isBelgianCompliant.value) {
    abortEvent.data.operatorFiscalId = operatorFiscalId;
    abortEvent.data.posDateTime = posDateTime;
  }

  LogService.info("Compliance: Processing abort event", { abortEvent });

  const result = await compliance.processEvent(EventType.ABORT, abortEvent);
  handleEventSent(result);
  if (!result.success) {
    await refreshFiscalDeviceStatus();
    throw result.error;
  }

  LogService.info(
    "Compliance: Abort event processed successfully:",
    result.data
  );
  return result;
}

/**
 * Parses the document/operation type from the EFR payload object
 * @param payload
 */
function parseDocumentxOperationType(payload: string) {
  if (!payload) {
    return { documentType: "", operationType: "" };
  }
  const parts = payload.split(",");
  const parsedDocumentType = parts[3].split(":")[0];
  const documentType = efrDict[parsedDocumentType] || parsedDocumentType;
  const operationType = parts[4];
  return { documentType, operationType };
}

/**
 * Gets company legal info
 */
export async function getCompanyLegalInfo() {
  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return;
  }
  const { compliance } = complianceData;

  const companyInfo = await compliance.getCompanyInfo();

  if (!companyInfo) {
    LogService.info("Compliance: No company info found in compliance module");
    return;
  }

  LogService.info("Compliance: Company info retrieved", { companyInfo });
  return companyInfo;
}

/**
 * Processes Money In/Out events
 * @param type
 * @param amount
 * @param description
 */
export async function processMoneyInOutEvent(data: {
  moneyInOutType: MoneyInOutType;
  amount: number;
  description: string;
  posDateTime: string;
  paymentType: PaymentMethod;
  cashLocation?: CashLocation;
}) {
  // Money in/out events are only processed for Germany, France and Belgium.
  // Croatia's Fiskalizacija does not use money in/out (cash transfer / opening
  // balance) events, so they are intentionally skipped here.
  if (
    !isGermanCompliant.value &&
    !isFrenchCompliant.value &&
    !isBelgianCompliant.value
  ) {
    return;
  }

  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return;
  }
  const {
    transactionNumber,
    operatorId,
    operatorName,
    operatorTin,
    operatorFiscalId,
    compliance,
    bookingPeriodId,
    posDateTime: generatedPosDateTime,
  } = complianceData;
  let { moneyInOutType, amount } = data;
  const { description, posDateTime, paymentType, cashLocation } = data;

  // France: amounts must always be positive, and DIFFERENCE is not supported
  if (isFrenchCompliant.value) {
    const isGain = amount >= 0;
    amount = Math.abs(amount);
    if (moneyInOutType === MoneyInOutType.DIFFERENCE) {
      moneyInOutType = isGain ? MoneyInOutType.PAY_IN : MoneyInOutType.PAY_OUT;
    }
  }

  // This is the only processor that takes posDateTime from its caller, and every
  // caller builds it as a UTC ISO string, which is what Germany and France read.
  // The FDM rejects that shape: it wants the POS wall-clock time with an explicit
  // offset, which is what generateFdmDateTime produces and what every other
  // Belgian event already sends. Reformat the caller's instant rather than
  // re-reading the clock, so the timestamp still marks when the money moved, and
  // fall back to the generated one if the caller's string does not parse.
  let eventPosDateTime = posDateTime;
  if (isBelgianCompliant.value) {
    const callerDate = new Date(posDateTime);
    if (Number.isNaN(callerDate.getTime())) {
      // Should not happen — every caller builds this with dayjs().toISOString().
      // Worth an error: the FDM would sign a time that is not when the money
      // actually moved.
      LogService.info(
        "Compliance: money in/out posDateTime could not be parsed, falling back to the current time",
        { posDateTime, moneyInOutType, generatedPosDateTime },
        LogLevel.Error
      );
      eventPosDateTime = generatedPosDateTime;
    } else {
      eventPosDateTime = generateFdmDateTime(callerDate);
    }
  }

  const event = {
    data: {
      type: moneyInOutType,
      amount,
      description,
      transactionNumber,
      operatorId,
      operatorName,
      // not reported in Belgium — see processAdditionalEvents
      ...(!isBelgianCompliant.value && { operatorTin }),
      operatorFiscalId,
      posDateTime: eventPosDateTime,
      paymentType,
      bookingPeriodId,
      location: cashLocation,
    },
  };
  LogService.info("Compliance: processing money in/out event", { event });
  const result = await compliance.processEvent(EventType.MONEY_IN_OUT, event);
  handleEventSent(result);
  if (!result.success) {
    LogService.info("Compliance: money in/out event error", {
      error: result.error,
    });
    await refreshFiscalDeviceStatus();
    throw new Error(result.error);
  }
  LogService.info("Compliance: money in/out processed", { result });
  return result;
}

/**
 * Processes Suspend/Resume receipt events (Belgium).
 *
 * France reports suspend/resume through `processAdditionalEvents` without an
 * order session; the Belgian FDM needs the full converted order session, hence
 * the separate processor.
 * @param booking Booking to process
 * @param orderSessionId Order session ID to process
 * @param type either suspend or resume
 */
export async function processSuspendAndResume(
  booking: IProductSlotBooking,
  orderSessionId: string,
  type: EventType.SUSPEND_RECEIPT | EventType.RESUME_RECEIPT
) {
  if (!isBelgianCompliant.value) {
    return;
  }
  const complianceData = await validateComplianceRequirements();
  if (!complianceData) {
    return;
  }
  const {
    transactionNumber,
    operatorId,
    operatorName,
    operatorFiscalId,
    posDateTime,
    compliance,
  } = complianceData;
  let orderSession;
  try {
    orderSession = await convertBookingToOrderSession(booking, orderSessionId);
  } catch (error) {
    LogService.info("Compliance: Failed to convert booking to order session", {
      error,
    });
    throw new Error(`Failed to convert booking to order session`);
  }

  if (!orderSession) {
    LogService.info(
      "Compliance: Undefined order session while processing suspend/resume, aborting"
    );
    throw new Error(
      `Undefined order session while processing suspend/resume, aborting`
    );
  }

  const eventTypeString =
    type === EventType.SUSPEND_RECEIPT ? "suspend" : "resume";

  const event = {
    data: {
      orderSession,
      transactionNumber,
      operatorId,
      operatorName,
      operatorFiscalId,
      posDateTime,
    },
  };

  LogService.info(`Compliance: Processing ${eventTypeString} event`, { event });

  const result = await compliance.processEvent(type, event);
  handleEventSent(result);
  if (!result.success) {
    // in case of error, test the connection again
    await testFdmConnection();
    throw result.error;
  }

  LogService.info(
    `Compliance: ${eventTypeString} processed successfully:`,
    result.data
  );
  return result;
}

export enum ComplianceErrorCodes {
  FDM_NOT_CONNECTED = "FDM_NOT_CONNECTED",
  EFR_NOT_CONNECTED = "EFR_NOT_CONNECTED",
  COMPLIANCE_LIB_INIT_FAILED = "COMPLIANCE_LIB_INIT_FAILED",
  DEVICE_NOT_ASSIGNED = "DEVICE_NOT_ASSIGNED",
  UNKNWON_ERROR = "UNKNOWN_ERROR",
}

/**
 * Translation of EFR document types TIK and DUP
 */
export const efrDict: { [key: string]: string } = {
  TIK: "Ticket",
  DUP: "Duplicata",
};

/**
 * VAT Rates (France)
 */
export const frVatRates: { [key: string]: string } = {
  "0.00%": "E",
  "2.10%": "D",
  "5.50%": "C",
  "10.00%": "B",
  "20.00%": "A",
};

export function mapCashLocation(
  locationId: number | null
): CashLocation | undefined {
  const locations = DeviceModule.locations;
  if (!locations.length || !locationId) {
    return;
  }
  const location = locations.find((loc) => loc.id === Number(locationId));

  if (!location || !location.name) {
    return;
  }

  const locationName = location.name.toLowerCase();

  if (
    locationName.startsWith("pos") ||
    locationName.startsWith("kassa") ||
    locationName.startsWith("kasse ") // 'kasse ' with space to avoid matching 'kassenladen'
  ) {
    return CashLocation.POS;
  } else if (locationName.startsWith("safe")) {
    return CashLocation.SAFE;
  } else if (locationName.startsWith("bank")) {
    return CashLocation.BANK;
  }
  return CashLocation.OTHER;
}

/**
 * VAT Rates (Spain)
 */
export const esVatRates: { [key: string]: string } = {
  "0.00%": "D",
  "4.00%": "C",
  "10.00%": "B",
  "21.00%": "A",
};

/**
 * VAT Rates (Croatia)
 */
export const hrVatRates: { [key: string]: string } = {
  "0.00%": "Z",
  "5.00%": "C",
  "13.00%": "B",
  "25.00%": "A",
};

/**
 * VAT Rates (Belgium)
 */
export const beVatRates: { [key: string]: string } = {
  "0.00%": "D",
  "6.00%": "C",
  "12.00%": "B",
  "21.00%": "A",
};
